import { computed, type ComputedRef } from "vue";
import { useKbd as useKbdMapping } from "@/runtime/composables/useKbd";
import { kbdClasses } from "./kbd.styles";
import { cardClasses } from "components/Card/card.styles";
import type { KbdProps } from "./kbd.types";

export interface UseKbdOptions {
  props: Readonly<KbdProps>;
}

export interface UseKbdReturn {
  classes: ComputedRef<string>;
  resolvedKey: ComputedRef<string | undefined>;
  iconName: ComputedRef<string | undefined>;
  shouldShowIcon: ComputedRef<boolean>;
  isSquare: ComputedRef<boolean>;
}

export const useKbd = (options: UseKbdOptions): UseKbdReturn => {
  const { props } = options;
  const { getKbdKey, getKbdIcon, hasKbdIcon } = useKbdMapping();

  const resolvedKey = computed(() => getKbdKey(props.value));
  const iconName = computed(() => getKbdIcon(props.value));
  const shouldShowIcon = computed(() => hasKbdIcon(props.value));

  const isSquare = computed(() => {
    if (shouldShowIcon.value && iconName.value) return true;
    const text = resolvedKey.value ?? props.value ?? "";
    return text.length === 1;
  });

  const classes = computed(() => {
    const card = cardClasses({ color: props.color, variant: props.variant, size: props.size });
    const kbd = kbdClasses({
      color: props.color,
      variant: props.variant,
      size: props.size,
      square: isSquare.value ? "true" : "false",
    });
    return `${card} ${kbd}`;
  });

  return { classes, resolvedKey, iconName, shouldShowIcon, isSquare };
};
